import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Calendar, CheckSquare, TrendingUp, Infinity, Zap, HeadphonesIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import DemoHabitGrid from "@/components/demo/DemoHabitGrid";
import DemoMoodTracker from "@/components/demo/DemoMoodTracker";
import DemoPinnedHabits from "@/components/demo/DemoPinnedHabits";
import { useEffect } from "react";
import { usePWARedirect } from "@/hooks/usePWARedirect";
import Footer from "@/components/layout/Footer";
import ScatteredGridBoxes from "@/components/hero/ScatteredGridBoxes";
import InstallAppSection from "@/components/pwa/InstallAppSection";
import UserInterviewBanner from "@/components/UserInterviewBanner";

export default function LandingPage() {
  const { user } = useAuth();

  // Auto-redirect to dashboard for signed-in PWA users
  usePWARedirect();

  useEffect(() => {
    document.title = "ADHD Habit Tracker & Daily Planner – Visual Grid Tracker | Habitualy";
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <UserInterviewBanner />
      
      <main className="flex-1">
        <section className="py-24 md:py-40 lg:py-48 bg-gradient-to-b from-background to-muted/30 relative">
          <ScatteredGridBoxes />
          <div className="container px-4 md:px-6 relative z-10">
            <div className="flex flex-col items-center space-y-4 text-center">
              <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl">
              Stop drifting. <span className="text-primary">See <br /> exactly what's shaping your days.</span>
              </h1>
              <p className="max-w-[700px] text-muted-foreground md:text-xl">
              A visual ADHD habit tracker and daily planner that respects how your brain works. No noise. No dopamine tricks. Just clarity and momentum.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                {user ? (
                  <Button size="lg" asChild>
                    <Link to="/dashboard">Go to Dashboard</Link>
                  </Button>
                ) : (
                  <>
                    <Button size="lg" asChild>
                      <Link to="/auth">Visualize Now</Link>
                    </Button>
                    <Button size="lg" variant="outline" asChild>
                      <a href="#demo">See Demo</a>
                    </Button>
                  </>
                )}
              </div>
              <div className="mt-4">
                <Link 
                  to="/blog" 
                  className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
                >
                  Read our blog →
                </Link>
              </div>
            </div>
          </div>
        </section>
        
        <section id="features" className="py-16 md:py-24">
          <div className="container px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">Everything you need to stay consistent and nothing you don’t</h2>
            <div className="grid gap-8 lg:grid-cols-3 lg:gap-12">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="bg-primary/10 p-4 rounded-full">
                  <CheckSquare
                    className="h-6 w-6 text-primary"
                  />
                </div>
                <h3 className="text-xl font-bold">Visual Progress Grid</h3>
                <p className="text-muted-foreground">
                Your week, visualized like your code commits but for your mind - a visual habit tracker built as a grid. The grid turns your routines into something you can actually see. Power days stand out.
                </p>
              </div>
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="bg-primary/10 p-4 rounded-full">
                  <Calendar
                    className="h-6 w-6 text-primary"
                  />
                </div>
                <h3 className="text-xl font-bold">Unlike most ADHD productivity apps, Habitualy stays quiet.</h3>
                <p className="text-muted-foreground">
                  No reminders screaming at you. No fake rewards. Just a quiet visual signal from your past self that pulls you forward instead of shaming you.
                </p>
              </div>
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="bg-primary/10 p-4 rounded-full">
                  <TrendingUp
                    className="h-6 w-6 text-primary"
                  />
                </div>
                <h3 className="text-xl font-bold">Mood Intelligence</h3>
                <p className="text-muted-foreground">
                  Track how you actually feel alongside what you do - habit and mood tracking in one visual system. Over time, patterns emerge — which habits stabilize you, and which ones quietly make things worse.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="demo" className="py-16 md:py-24 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1 text-center lg:text-left space-y-4">
                <h2 className="text-2xl md:text-3xl font-bold">Stop Guessing</h2>
                <p className="text-muted-foreground md:text-lg">
                  Habits don’t live alone. They stack, clash, and amplify each other. The combined grid shows how your routines interact over time. Patterns you’d never catch in a checklist. This combined grid turns Habitualy into more than a habit tracker — it becomes a visual map of your behavior.
                </p>
                <p className="text-muted-foreground">
                  Darker days mean more follow-through. Lighter days warn you early before things spiral.
                </p>
              </div>
              <div className="flex-1 w-full max-w-[min(100%,560px)]">
                <Card className="p-6">
                  <DemoHabitGrid />
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
              <div className="flex-1 text-center lg:text-left space-y-4">
                <h2 className="text-2xl md:text-3xl font-bold">Pin What Matters Most</h2>
                <p className="text-muted-foreground md:text-lg">
                  Not all habits deserve equal attention. Especially for ADHD minds, fewer habits done well beat dozens done poorly. Pin the few that matter most — the ones that stabilize everything else. Each gets its own grid so you never lose sight of your foundations.
                </p>
                <p className="text-muted-foreground">
                  No scrolling. No clutter. Just what matters most.
                </p>
              </div>
              <div className="flex-1 w-full max-w-[min(100%,560px)]">
                <Card className="p-6">
                  <DemoPinnedHabits />
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1 text-center lg:text-left space-y-4">
                <h2 className="text-2xl md:text-3xl font-bold">Your emotional trajectory</h2>
                <p className="text-muted-foreground md:text-lg">
                  Spot slow declines before they turn into burnout. Many people who ask “am I neurodivergent?” recognize themselves in these patterns. See which habits correlate with calmer, better days — and which ones just look good on paper.
                </p>
                <p className="text-muted-foreground">
                  Did skipping your workout lead to a rough week? Does journaling correlate with better days? Over time, you'll discover which habits actually move the needle on your wellbeing.
                </p>
              </div>
              <div className="flex-1 w-full max-w-[min(100%,560px)]">
                <Card className="p-6">
                  <DemoMoodTracker />
                </Card>
              </div>
            </div>
            <div className="text-center mt-12">
              <Button size="lg" asChild>
                <Link to="/auth">See My Patterns</Link>
              </Button>
            </div>
          </div>
        </section>
        
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <h2 className="text-3xl font-bold tracking-tighter md:text-4xl">Take control of your habits</h2>
              <p className="max-w-[700px] text-muted-foreground md:text-xl">
                Start building powerful habits for free, or unlock complete control with our lifetime plan. No subscriptions, no hidden fees.
              </p>
              <div className="grid gap-8 md:grid-cols-2 lg:gap-12 mt-8">
                <div className="flex flex-col items-center space-y-4 p-6 rounded-xl bg-card border">
                  <h3 className="text-xl font-bold">Try it. Feel the difference.</h3>
                  <p className="text-3xl font-bold">Free</p>
                  <p className="text-sm text-muted-foreground">Everything you need to build <br></br>awareness and momentum.</p>
                  <ul className="space-y-2 text-left w-full">
                    <li className="flex items-center gap-2">
                      <CheckSquare className="h-5 w-5 text-primary" />
                      <span>3 core habits</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckSquare className="h-5 w-5 text-primary" />
                      <span>Essential tracking</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckSquare className="h-5 w-5 text-primary" />
                      <span>Beautiful themes</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckSquare className="h-5 w-5 text-primary" />
                      <span>Export your data</span>
                    </li>
                  </ul>
                  <Button size="lg" className="w-full" variant="outline" asChild>
                    <Link to="/auth">Start Without Commitment</Link>
                  </Button>
                </div>
                <div className="flex flex-col items-center space-y-4 p-6 rounded-xl bg-primary/5 border-2 border-primary">
                  <div className="bg-primary/10 px-3 py-1 rounded-full text-sm font-medium text-primary">
                    Complete Control
                  </div>
                  <h3 className="text-xl font-bold">Master</h3>
                  <div className="flex items-baseline gap-2 justify-center">
                    <p className="text-3xl font-bold">$19</p>
                  </div>
                  <p className="text-sm text-muted-foreground">Full control for those who take consistency <br></br>seriously. One-time payment.</p>
                  <ul className="space-y-2 text-left w-full">
                    <li className="flex items-center gap-2">
                      <Infinity className="h-5 w-5 text-primary" />
                      <span>Unlimited habits</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-primary" />
                      <span>Custom colors & organization</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-primary" />
                      <span>Advanced insights</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <HeadphonesIcon className="h-5 w-5 text-primary" />
                      <span>Priority support</span>
                    </li>
                  </ul>
                  <Button size="lg" className="w-full" asChild>
                    <Link to="/pricing">Commit to Consistency</Link>
                  </Button>
                </div>
              </div>
              <p className="max-w-[700px] text-muted-foreground text-[10px] md:text-xs">
                If you can not afford the app but still would like premium access for your own journey, send us an email <a href="mailto:abdullah@mon10.org" className="underline">here</a> letting us know your situation and we'll gift you a membership
              </p>
            </div>
          </div>
        </section>

        <InstallAppSection />

        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <h2 className="text-3xl font-bold tracking-tighter md:text-4xl">Build a life that feels under control.</h2>
              <p className="max-w-[700px] text-muted-foreground md:text-xl">
                Hundreds of people use Habitualy to reduce mental clutter, stay consistent, and feel calmer about their days. You can too - with a visual habit tracker made for real brains.
              </p>
              <ul className="flex flex-wrap justify-center gap-4 mt-4">
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-primary" fill="none" height="24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="24">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  <span>Start free</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-primary" fill="none" height="24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="24">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  <span>No credit card</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-primary" fill="none" height="24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="24">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  <span>Privacy-first</span>
                </li>
              </ul>
              <Button size="lg" className="mt-6" asChild>
                <Link to="/auth">Start for Free</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
